import React from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import {
  LightBulbIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
  CogIcon,
  BoltIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';

// Define best practices articles with their metadata
const bestPractices = [
  {
    id: 'avoid-hardcoded-ids',
    title: 'Avoid Hardcoded IDs',
    description: 'Learn why hardcoded IDs are problematic and how to build flexible, environment-independent flows',
    icon: ExclamationTriangleIcon,
    category: 'Development',
    difficulty: 'Intermediate',
    readTime: '8 min',
    topics: ['Record IDs', 'Environment Migration', 'Dynamic References']
  },
  {
    id: 'user-assignment',
    title: 'Dynamic User Assignment',
    description: 'Best practices for assigning records to users using queues and dynamic lookups instead of hardcoded user IDs',
    icon: UserGroupIcon,
    category: 'User Management',
    difficulty: 'Intermediate',
    readTime: '6 min',
    topics: ['Queues', 'User Lookups', 'Assignment Rules']
  },
  {
    id: 'record-type-assignment',
    title: 'Record Type Best Practices',
    description: 'Query record types by DeveloperName for reliable, environment-independent record creation',
    icon: DocumentTextIcon,
    category: 'Data Management',
    difficulty: 'Beginner',
    readTime: '5 min',
    topics: ['Record Types', 'DeveloperName', 'Get Records']
  },
  {
    id: 'custom-metadata',
    title: 'Using Custom Metadata Types',
    description: 'Store configuration values in Custom Metadata Types for flexible, deployable flow configuration',
    icon: CogIcon,
    category: 'Configuration',
    difficulty: 'Advanced',
    readTime: '10 min',
    topics: ['Custom Metadata', 'Configuration', 'Deployment']
  },
  {
    id: 'entry-criteria',
    title: 'Optimizing Entry Criteria',
    description: 'Write efficient entry criteria to improve flow performance and reduce unnecessary executions',
    icon: BoltIcon,
    category: 'Performance',
    difficulty: 'Intermediate',
    readTime: '7 min',
    topics: ['Entry Criteria', 'Performance', 'Record-Triggered Flows']
  }
];

// Group practices by category
const categorizedPractices = bestPractices.reduce((acc, practice) => {
  if (!acc[practice.category]) {
    acc[practice.category] = [];
  }
  acc[practice.category].push(practice);
  return acc;
}, {} as Record<string, typeof bestPractices>);

export default function BestPracticesPage() {
  const { orgId } = useParams();
  const basePath = orgId ? `/${orgId}` : '';
  
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to={`${basePath}/help`} className="text-gray-500 hover:text-gray-700">
                Help Center
              </Link>
            </li>
            <li className="text-gray-500">/</li>
            <li className="text-gray-900">Best Practices</li>
          </ol>
        </nav>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <LightBulbIcon className="h-8 w-8 text-yellow-500 mr-3" />
            Flow Development Best Practices
          </h1>
          <p className="mt-2 text-lg text-gray-600">
            Learn proven techniques for building maintainable, scalable, and efficient Salesforce flows
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-4 text-center">
            <p className="text-2xl font-semibold text-gray-900">{bestPractices.length}</p>
            <p className="text-sm text-gray-600">Best Practice Guides</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 text-center">
            <p className="text-2xl font-semibold text-gray-900">{Object.keys(categorizedPractices).length}</p>
            <p className="text-sm text-gray-600">Categories</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 text-center">
            <p className="text-2xl font-semibold text-gray-900">
              {bestPractices.reduce((sum, p) => sum + parseInt(p.readTime), 0)} min
            </p>
            <p className="text-sm text-gray-600">Total Reading Time</p>
          </div>
        </div>

        {/* Featured Best Practice */}
        <div className="mb-12 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
          <div className="flex items-start justify-between">
            <div>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 mb-3">
                Featured Guide
              </span>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                {bestPractices[0].title}
              </h2>
              <p className="text-gray-700 mb-4">
                {bestPractices[0].description}
              </p>
              <Link
                to={`${basePath}/help/best-practices/${bestPractices[0].id}`}
                className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
              >
                Read Guide
                <ArrowRightIcon className="ml-2 h-4 w-4" />
              </Link>
            </div>
            <ExclamationTriangleIcon className="h-12 w-12 text-blue-600 flex-shrink-0" />
          </div>
        </div>

        {/* Best Practices by Category */}
        {Object.entries(categorizedPractices).map(([category, practices]) => (
          <div key={category} className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">{category}</h2>
            <div className="grid gap-4">
              {practices.map((practice) => {
                const Icon = practice.icon;
                return (
                  <Link
                    key={practice.id}
                    to={`${basePath}/help/best-practices/${practice.id}`}
                    className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow duration-200 border border-gray-200"
                  >
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <Icon className="h-6 w-6 text-gray-600" />
                      </div>
                      <div className="ml-4 flex-1">
                        <div className="flex items-center justify-between">
                          <h3 className="text-lg font-medium text-gray-900">
                            {practice.title}
                          </h3>
                          <div className="flex items-center space-x-2 text-sm text-gray-500">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              practice.difficulty === 'Beginner' ? 'bg-green-100 text-green-800' :
                              practice.difficulty === 'Intermediate' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {practice.difficulty}
                            </span>
                            <span>{practice.readTime} read</span>
                          </div>
                        </div>
                        <p className="mt-1 text-gray-600">
                          {practice.description}
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {practice.topics.map((topic) => (
                            <span
                              key={topic}
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                            >
                              {topic}
                            </span>
                          ))}
                        </div>
                      </div>
                      <ArrowRightIcon className="ml-4 h-5 w-5 text-gray-600 flex-shrink-0" />
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        ))}

        {/* Call to Action */}
        <div className="mt-12 bg-gray-100 rounded-lg p-6 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Have a best practice to share?
          </h3>
          <p className="text-gray-600 mb-4">
            We're always looking to expand our best practices library with community contributions.
          </p>
          <a
            href="mailto:support@getflowdocs.com?subject=Best Practice Suggestion"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            Submit a Best Practice
          </a>
        </div>
      </div>
    </div>
  );
}